import { toAskError } from '@/services/ask/errors';
import type { components } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';

type Schemas = components['schemas'];

export type AskConversationSnapshot = Schemas['AskConversationSnapshot']['data'];

export type AskPrimaryCancerType = NonNullable<AskConversationSnapshot['primaryCancerType']>;

export type AskTurn = AskConversationSnapshot['turns'][number];

export type AskTurnStatus = AskTurn['status'];

export type AskTurnStage = NonNullable<AskTurn['stage']>;

export type AskTurnContext = NonNullable<AskTurn['context']>;

export type AskResult = AskTurn['results'][number];

export type AskAvailableResult = Extract<AskResult, { unavailable: false }>;

export type AskConversationPage = Schemas['AskConversationList']['data'];

export type AskConversationSummary = AskConversationPage['conversations'][number];

export type AskResultDetail = Schemas['AskResultDetail']['data'];

/** A published Inquiry Library post on a result's question. The Ask API never carries its body. */
export type AskResultPost = AskAvailableResult['posts'][number];

export type AskSubmitTurnBody = Schemas['AskSubmitTurnRequest'];

export type AskSubmittedTurn = Schemas['AskSubmitTurnResponse']['data']['turn'];

export type AskRenamedConversation = Schemas['AskRenameConversationResponse']['data'];

/** The server trims before checking, so the app measures the same way. */
export const ASK_TEXT_MIN_LENGTH = 10;

export const ASK_TEXT_MAX_LENGTH = 8000;

/**
 * Throws an `AskError` for anything other than a successful body, so callers
 * switch on one type whichever way the call failed.
 */
async function call<T>(request: () => Promise<{ data?: { data: T } }>): Promise<T> {
  try {
    const { data } = await request();

    if (!data) {
      throw new Error('Ask API returned no data');
    }

    return data.data;
  } catch (error) {
    throw toAskError(error);
  }
}

export function getAskConversations(cursor: string | undefined): Promise<AskConversationPage> {
  return call(() =>
    fetchClient.GET('/v2/ask/conversations', { params: { query: cursor ? { cursor } : {} } })
  );
}

export function getAskConversation(conversationId: string): Promise<AskConversationSnapshot> {
  return call(() =>
    fetchClient.GET('/v2/ask/conversations/{conversationId}', {
      params: { path: { conversationId } },
    })
  );
}

export function getAskResult({
  conversationId,
  turnId,
  source,
  resultId,
}: {
  conversationId: string;
  turnId: string;
  source: string;
  resultId: string;
}): Promise<AskResultDetail> {
  return call(() =>
    fetchClient.GET(
      '/v2/ask/conversations/{conversationId}/turns/{turnId}/results/{source}/{resultId}',
      { params: { path: { conversationId, turnId, source, resultId } } }
    )
  );
}

/**
 * Asks a question. `202` (started) and `200` (this turn id already existed) are
 * both success, which is what makes resending under the same `turnId` safe.
 */
export async function putAskTurn({
  conversationId,
  turnId,
  body,
}: {
  conversationId: string;
  turnId: string;
  body: AskSubmitTurnBody;
}): Promise<AskSubmittedTurn> {
  const data = await call(() =>
    fetchClient.PUT('/v2/ask/conversations/{conversationId}/turns/{turnId}', {
      params: { path: { conversationId, turnId } },
      body,
    })
  );

  return data.turn;
}

export function patchAskConversation({
  conversationId,
  title,
  acknowledgedIdentifierWarning,
}: {
  conversationId: string;
  title: string;
  acknowledgedIdentifierWarning?: boolean;
}): Promise<AskRenamedConversation> {
  return call(() =>
    fetchClient.PATCH('/v2/ask/conversations/{conversationId}', {
      params: { path: { conversationId } },
      body: {
        title,
        ...(acknowledgedIdentifierWarning && { acknowledgedIdentifierWarning: true }),
      },
    })
  );
}

export async function deleteAskConversation(conversationId: string): Promise<void> {
  try {
    await fetchClient.DELETE('/v2/ask/conversations/{conversationId}', {
      params: { path: { conversationId } },
    });
  } catch (error) {
    throw toAskError(error);
  }
}
